import React, { useState, useCallback } from 'react';
import { InputButton } from './InputButton';
import type { ToolGroup } from './config';
import { useChatStore } from '../../store/chatStore';

/**
 * ToolSelector component for selecting tools (e.g., Plerion, Vanta).
 * This is designed for extensibility and clear UX, using ToolGroup config and selectedToolGroups state.
 */
interface ToolSelectorProps {
  tools: ToolGroup[];
  maxVisibleTools?: number;
  selectedToolGroups: string[];
  onChange?: (selectedToolGroups: string[]) => void;
  dropUpMenu?: boolean;
}

export const ToolSelector: React.FC<ToolSelectorProps> = React.memo(
  ({
    tools,
    maxVisibleTools = 2,
    selectedToolGroups,
    onChange,
    dropUpMenu = false,
  }: ToolSelectorProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const inputRef = useChatStore((state) => state.activeInputRef);
    // Mark tools as active based on selectedToolGroups
    const toolsWithActive = tools.map((tool) => ({
      ...tool,
      isActive: selectedToolGroups.includes(tool.id),
    }));

    // Get visible and dropdown tools based on active state and maxVisibleTools
    const visibleTools = toolsWithActive
      .filter((tool) => tool.isActive)
      .concat(toolsWithActive.filter((tool) => !tool.isActive))
      .slice(0, maxVisibleTools);
    const dropdownTools = toolsWithActive.filter(
      (tool) => !visibleTools.includes(tool),
    );

    // Count active items in dropdown
    const activeDropdownCount = dropdownTools.filter(
      (tool) => tool.isActive,
    ).length;

    const handleToggle = useCallback(
      (toolToToggle: ToolGroup) => {
        let newSelected: string[];
        if (selectedToolGroups.includes(toolToToggle.id)) {
          newSelected = selectedToolGroups.filter(
            (id) => id !== toolToToggle.id,
          );
        } else {
          newSelected = [...selectedToolGroups, toolToToggle.id];
        }
        onChange?.(newSelected);
        setIsDropdownOpen(false);
        inputRef?.current?.focus();
      },
      [onChange, selectedToolGroups, inputRef],
    );

    return (
      <div className="flex items-center gap-2">
        {visibleTools.map((tool) => (
          <InputButton
            key={tool.id}
            icon={tool.icon}
            label={tool.label}
            onClick={() => handleToggle(tool)}
            isActive={tool.isActive}
          />
        ))}

        {dropdownTools.length > 0 && (
          <div className="relative">
            <div className="relative">
              <InputButton
                icon={
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
                    />
                  </svg>
                }
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
              {activeDropdownCount > 0 && (
                <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FF732E] text-xs font-medium text-white">
                  {activeDropdownCount}
                </div>
              )}
            </div>

            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                />
                <div
                  className={`absolute -right-10 z-20 min-w-[200px] rounded-xl border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${
                    dropUpMenu ? 'bottom-full mb-2' : 'top-full mt-2'
                  }`}
                >
                  {dropdownTools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => handleToggle(tool)}
                      className={`flex w-full items-center gap-2 px-4 py-1 text-sm transition-colors ${
                        tool.isActive
                          ? 'bg-[#FF732E]/10 text-[#FF732E] hover:bg-[#FF732E]/20'
                          : 'border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {tool.icon}
                      {tool.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

ToolSelector.displayName = 'ToolSelector';
